import { callable } from "./firebase";

export type OnboardingStatus = "pending" | "signed" | "paid" | "complete";

export type BillingPlan = "weekly" | "monthly";

export interface ProbationOfficerSummary {
  uid: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface OnboardingPrefill {
  client: {
    fullName: string | null;
    dateOfBirth: string | null;
    addressLine: string | null;
    city: string | null;
    state: string | null;
    zipcode: string | null;
    phone: string | null;
    email: string | null;
  };
  work: {
    employerName: string | null;
    employerAddress: string | null;
    employerPhone: string | null;
  };
  probationOfficers: ProbationOfficerSummary[];
}

export interface OnboardingSession {
  uid: string;
  email: string | null;
  displayName: string | null;
  onboardingStatus: OnboardingStatus;
  leaseTemplateUrls: Record<BillingPlan, string>;
  /**
   * Self-signup: FF app login password (same as pinCode / Firebase Auth).
   * Null for admin-invited users.
   */
  password: string | null;
  prefill: OnboardingPrefill;
  pricing: OnboardingPricing;
}

/**
 * Returned by `createOnboardingAccount` — extends OnboardingSession with the
 * freshly minted JWT so the web page can immediately render the Sign step
 * without a second call to `getOnboardingSession`.
 */
export interface CreateAccountResp extends OnboardingSession {
  token: string;
}

export interface AuthedReq {
  uid: string;
  token: string;
}

export async function fetchOnboardingSession(req: AuthedReq): Promise<OnboardingSession> {
  const fn = callable<AuthedReq, OnboardingSession>("getOnboardingSession");
  const { data } = await fn(req);
  return data;
}

export interface CreatePaymentIntentReq extends AuthedReq {
  /** Optional coupon code to apply (case-insensitive). */
  couponCode?: string;
  billingPlan: BillingPlan;
}

export interface CreatePaymentIntentResp {
  clientSecret: string;
  paymentIntentId: string;
  customerId: string;
  status: string;
  amount: number;
  currency: string;
  /** Echoes the canonical coupon string back if valid, else null. */
  couponApplied: string | null;
  /** $ that would have been charged but is waived by the coupon (in cents). */
  activationFeeWaivedCents: number;
  billingPlan: BillingPlan;
}

export async function createPaymentIntent(
  req: CreatePaymentIntentReq,
): Promise<CreatePaymentIntentResp> {
  const fn = callable<CreatePaymentIntentReq, CreatePaymentIntentResp>(
    "createOnboardingPaymentIntent",
  );
  const { data } = await fn(req);
  return data;
}

export interface ExhibitAClient {
  fullName: string;
  dateOfBirth: string;
  addressLine: string;
  city: string;
  state: string;
  zipcode: string;
  phone: string;
  email: string;
  /** Optional court case number. */
  caseNumber: string | null;
}

export interface ExhibitAWork {
  employerName: string;
  employerAddress: string;
  employerPhone: string;
  supervisorName: string;
  supervisorPhone: string;
}

/** A single known contact entry on the Exhibit A. 1-4 captured; first 2 stamped on PDF. */
export interface KnownContact {
  name: string;
  phone: string;
  relationship: string | null;
}

export interface ExhibitAPayload {
  client: ExhibitAClient;
  probationOfficer: {
    uid: string | null;
    name: string;
    phone: string | null;
  };
  work: ExhibitAWork;
  knownContacts: KnownContact[];
  attestation: {
    accurate: boolean;
    accurateAt: string;
    agreeTerms: boolean;
    agreeTermsAt: string;
  };
}

export interface CompleteOnboardingReq extends AuthedReq {
  paymentIntentId: string;
  signedPdfBase64: string;
  signerFullName: string;
  exhibitA: ExhibitAPayload;
}

export interface CompleteOnboardingResp {
  ok: true;
  onboardingStatus: OnboardingStatus;
  subscriptionId: string;
  trialEndsAtUnix: number | null;
  leasePath: string;
  billingPlan: BillingPlan;
}

export async function completeOnboarding(
  req: CompleteOnboardingReq,
): Promise<CompleteOnboardingResp> {
  const fn = callable<CompleteOnboardingReq, CompleteOnboardingResp>(
    "completeOnboarding",
  );
  const { data } = await fn(req);
  return data;
}

// ============================================================================
// Public self-signup
// ============================================================================

export interface County {
  id: string;
  state: string;
  county: string;
}

export interface ListCountiesResp {
  counties: County[];
}

function isRetryableCallableError(err: unknown): boolean {
  const code = String((err as { code?: string })?.code ?? "");
  const msg = String((err as Error)?.message ?? err).toLowerCase();
  return (
    code.includes("deadline-exceeded") ||
    code.includes("unavailable") ||
    code.includes("internal") ||
    msg.includes("deadline-exceeded") ||
    msg.includes("unavailable")
  );
}

async function callWithRetry<T>(fn: () => Promise<T>, attempts = 2): Promise<T> {
  let last: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      last = err;
      if (!isRetryableCallableError(err) || i === attempts - 1) throw err;
    }
  }
  throw last;
}

export async function listCounties(): Promise<ListCountiesResp> {
  return callWithRetry(async () => {
    const fn = callable<Record<string, never>, ListCountiesResp>("listCounties");
    const { data } = await fn({});
    return data;
  });
}

export interface Officer {
  uid: string;
  display_name: string;
  county: string;
  state: string | null;
  phone_number: string | null;
}

export interface ListOfficersResp {
  officers: Officer[];
}

export async function listOfficers(county: string): Promise<ListOfficersResp> {
  return callWithRetry(async () => {
    const fn = callable<{ county: string }, ListOfficersResp>("listOfficers");
    const { data } = await fn({ county });
    return data;
  });
}

export interface CreateOnboardingAccountReq {
  email: string;
  displayName: string;
  county: string;
  state: string;
  officerUid?: string;
}

export async function createOnboardingAccount(
  req: CreateOnboardingAccountReq,
): Promise<CreateAccountResp> {
  const fn = callable<CreateOnboardingAccountReq, CreateAccountResp>(
    "createOnboardingAccount",
  );
  const { data } = await fn(req);
  return data;
}

// ============================================================================
// Deferred self-signup — account is created AFTER payment succeeds
// ============================================================================

export interface OnboardingPricing {
  currency: string;
  firstMonthCents: number;
  activationFeeCents: number;
  totalDueTodayCents: number;
  recurringCents: Record<BillingPlan, number>;
  trialDays: number;
}

export interface PublicOnboardingConfig {
  leaseTemplateUrls: Record<BillingPlan, string>;
  pricing: OnboardingPricing;
}

export async function getPublicOnboardingConfig(): Promise<PublicOnboardingConfig> {
  const fn = callable<Record<string, never>, PublicOnboardingConfig>(
    "getPublicOnboardingConfig",
  );
  const { data } = await fn({});
  return data;
}

export interface CreatePendingPaymentIntentReq {
  email: string;
  displayName: string;
  county: string;
  state: string;
  officerUid?: string;
  signedPdfBase64: string;
  signerFullName: string;
  exhibitA: ExhibitAPayload;
  /** Pass an existing PI id to update (coupon toggle) instead of creating fresh. */
  paymentIntentId?: string;
  couponCode?: string;
  billingPlan: BillingPlan;
}

export type CheckoutMode = "payment" | "setup";

export interface CreatePendingPaymentIntentResp {
  clientSecret: string;
  paymentIntentId: string;
  customerId: string;
  status: string;
  amount: number;
  currency: string;
  couponApplied: string | null;
  activationFeeWaivedCents: number;
  /** "setup" saves a card for a partner code and charges $0 today. */
  checkoutMode?: CheckoutMode;
  partnerName?: string | null;
  partnerCode?: string | null;
  billingPlan: BillingPlan;
}

export async function createPendingPaymentIntent(
  req: CreatePendingPaymentIntentReq,
): Promise<CreatePendingPaymentIntentResp> {
  const fn = callable<
    CreatePendingPaymentIntentReq,
    CreatePendingPaymentIntentResp
  >("createPendingPaymentIntent");
  const { data } = await fn(req);
  return data;
}

export interface FinalizePendingSignupResp {
  uid: string;
  token: string;
  email: string;
  password: string;
  alreadyFinalized: boolean;
}

export async function finalizePendingSignup(
  paymentIntentId: string,
): Promise<FinalizePendingSignupResp> {
  const fn = callable<
    { paymentIntentId: string },
    FinalizePendingSignupResp
  >("finalizePendingSignup");
  const { data } = await fn({ paymentIntentId });
  return data;
}
